import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

import { promotionKeys } from "./query-keys";

type Promotion = components["schemas"]["Promotion"];
type Target = components["schemas"]["StatusTransitionRequest"]["target"];

export interface TransitionVars {
  id: string;
  target: Target;
}

/**
 * POST /v1/promotions/{id}/transition. Optimistic flip (spec §6: ActivateToggle)
 * — on error (e.g. 422 activation pre-check fail) the cached promotion reverts.
 */
export function useTransitionStatus() {
  const qc = useQueryClient();
  return useMutation({
    // Call site (PromotionsPage) already renders its own error UI (onActivateErr);
    // suppress the global toast to avoid a duplicate raw-error message (TC58).
    meta: { silentError: true },
    mutationFn: async ({ id, target }: TransitionVars) => {
      const { data, error } = await apiClient.POST("/v1/promotions/{id}/transition", {
        params: { path: { id } },
        body: { target },
      });
      /* v8 ignore next -- unreachable: client errorMiddleware throws ApiError on !ok */
      if (error) throw error;
      return data;
    },
    onMutate: async ({ id, target }) => {
      await qc.cancelQueries({ queryKey: promotionKeys.promotion(id) });
      const previous = qc.getQueryData<Promotion>(promotionKeys.promotion(id));
      qc.setQueryData<Promotion>(promotionKeys.promotion(id), (old) =>
        old ? { ...old, status: target } : old,
      );
      return { previous };
    },
    onError: (_err, { id }, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(promotionKeys.promotion(id), ctx.previous);
      }
    },
    onSettled: (_data, _err, { id }) => {
      void qc.invalidateQueries({ queryKey: promotionKeys.promotion(id) });
      void qc.invalidateQueries({ queryKey: promotionKeys.all });
    },
  });
}
